/**
 * Surgical markdown editing.
 *
 * The hard rule of this file: we NEVER parse markdown to an AST and
 * re-serialize it. The vault is written by hand and by AI assistants, and a
 * round-trip through a formatter would silently restyle everything — reordering
 * YAML keys, stripping comments, normalising list markers, collapsing blank
 * lines. The diff would be unreviewable and trust in the system would be gone.
 *
 * Instead every mutation touches the smallest possible span:
 *   - frontmatter edits go through YAML's document API and replace only the
 *     bytes between the `---` fences
 *   - task edits replace exactly one line, matched by index AND content
 *
 * Everything else in the file comes out byte-identical. `git diff` after a
 * checkbox toggle should be a single `-`/`+` pair — that diff is the real test
 * of this module.
 */

import { parseDocument, isMap } from 'yaml';

/** `---\n ... \n---` at the very top of the file. `m` so the closing fence anchors to a line start. */
const FRONTMATTER_RE = /^(﻿)?---[ \t]*\r?\n([\s\S]*?)^---[ \t]*(?:\r?\n|$)/m;

export interface FrontmatterSpan {
  /** Offset of the opening `---`. */
  start: number;
  /** Offset just past the closing fence's trailing newline. */
  end: number;
  /** The YAML source between the fences, without either fence. */
  text: string;
}

export interface ParsedFile {
  /** Parsed frontmatter, or `{}` when the file has none. */
  data: Record<string, unknown>;
  /** Everything after the closing fence. */
  body: string;
  raw: string;
  frontmatter: FrontmatterSpan | null;
  /** Line ending used by this file, so writes don't flip a file to the other style. */
  eol: '\n' | '\r\n';
  /** Populated when the frontmatter exists but is not valid YAML. */
  error?: string;
}

export function detectEol(raw: string): '\n' | '\r\n' {
  const firstLf = raw.indexOf('\n');
  if (firstLf > 0 && raw[firstLf - 1] === '\r') return '\r\n';
  return '\n';
}

export function parseFile(raw: string): ParsedFile {
  const eol = detectEol(raw);
  const match = FRONTMATTER_RE.exec(raw);

  if (!match) {
    return { data: {}, body: raw, raw, frontmatter: null, eol };
  }

  const bom = match[1] ?? '';
  const text = match[2] ?? '';
  const start = bom.length;
  const end = match.index + match[0].length;
  const frontmatter: FrontmatterSpan = { start, end, text };
  const body = raw.slice(end);

  try {
    const doc = parseDocument(text);
    // `yaml` collects syntax errors on the document rather than throwing, so a
    // malformed block would otherwise parse to `{}` and surface downstream as a
    // baffling "missing type" instead of "your YAML is broken on line 3".
    if (doc.errors.length > 0) {
      const first = doc.errors[0]!;
      return { data: {}, body, raw, frontmatter, eol, error: first.message };
    }

    const parsed = doc.toJS({ maxAliasCount: 100 });
    const data =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    return { data, body, raw, frontmatter, eol };
  } catch (err) {
    return {
      data: {},
      body,
      raw,
      frontmatter,
      eol,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Set keys in the frontmatter, leaving comments, key order, and every byte
 * outside the fences untouched. Creates a frontmatter block if absent.
 * A value of `undefined` deletes the key.
 */
export function patchFrontmatter(raw: string, patch: Record<string, unknown>): string {
  const parsed = parseFile(raw);
  const { eol } = parsed;

  if (!parsed.frontmatter) {
    const doc = parseDocument('');
    doc.contents = doc.createNode({}) as never;
    applyPatch(doc, patch);
    const yamlText = normaliseEol(doc.toString().trimEnd(), eol);
    return `---${eol}${yamlText}${eol}---${eol}${raw}`;
  }

  const doc = parseDocument(parsed.frontmatter.text);
  if (!isMap(doc.contents)) {
    doc.contents = doc.createNode({}) as never;
  }
  applyPatch(doc, patch);

  const yamlText = normaliseEol(doc.toString().trimEnd(), eol);
  const before = raw.slice(0, parsed.frontmatter.start);
  const after = raw.slice(parsed.frontmatter.end);
  return `${before}---${eol}${yamlText}${eol}---${eol}${after}`;
}

function applyPatch(doc: ReturnType<typeof parseDocument>, patch: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) doc.delete(key);
    else doc.set(key, value);
  }
}

function normaliseEol(text: string, eol: '\n' | '\r\n'): string {
  const unix = text.replace(/\r\n/g, '\n');
  return eol === '\n' ? unix : unix.replace(/\n/g, '\r\n');
}

// ─── Task lists ─────────────────────────────────────────────────────────────

const TASK_RE = /^(\s*)([-*+])(\s+)\[([ xX])\](\s+)(.*)$/;

export interface Task {
  /** 0-based index into the file's lines. Stable only for the content it was read from. */
  line: number;
  checked: boolean;
  text: string;
  indent: string;
  marker: string;
}

export function splitLines(raw: string): string[] {
  return raw.split(/\r?\n/);
}

export function parseTasks(raw: string): Task[] {
  const tasks: Task[] = [];
  const lines = splitLines(raw);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line === undefined) continue;
    const m = TASK_RE.exec(line);
    if (!m) continue;
    tasks.push({
      line: i,
      indent: m[1] ?? '',
      marker: m[2] ?? '-',
      checked: (m[4] ?? ' ').toLowerCase() === 'x',
      text: m[6] ?? '',
    });
  }
  return tasks;
}

/**
 * Toggle one checkbox. `expectedText` guards against the file having shifted
 * under us — if the line no longer holds that task, we refuse rather than
 * corrupting a neighbour. That guard is what makes conflict replay safe.
 */
export function setTaskChecked(
  raw: string,
  line: number,
  checked: boolean,
  expectedText?: string,
): string {
  const eol = detectEol(raw);
  const lines = splitLines(raw);
  const target = lines[line];
  if (target === undefined) throw new Error(`No line ${line} in file`);

  const m = TASK_RE.exec(target);
  if (!m) throw new Error(`Line ${line} is not a task: ${JSON.stringify(target)}`);
  if (expectedText !== undefined && (m[6] ?? '') !== expectedText) {
    throw new Error(
      `Line ${line} changed: expected ${JSON.stringify(expectedText)}, found ${JSON.stringify(m[6] ?? '')}`,
    );
  }

  lines[line] = `${m[1] ?? ''}${m[2] ?? '-'}${m[3] ?? ' '}[${checked ? 'x' : ' '}]${m[5] ?? ' '}${m[6] ?? ''}`;
  return lines.join(eol);
}

/** Append a task to the end of the file, matching the style of the last task present. */
export function appendTask(raw: string, text: string, checked = false): string {
  const eol = detectEol(raw);
  const existing = parseTasks(raw);
  const last = existing[existing.length - 1];
  const indent = last?.indent ?? '';
  const marker = last?.marker ?? '-';
  const entry = `${indent}${marker} [${checked ? 'x' : ' '}] ${text}`;

  if (raw === '') return entry;
  const needsBreak = !raw.endsWith('\n') && !raw.endsWith('\r\n');
  return needsBreak ? `${raw}${eol}${entry}` : `${raw}${entry}`;
}

/** Remove exactly one task line, guarded by its text. */
export function removeTask(raw: string, line: number, expectedText?: string): string {
  const eol = detectEol(raw);
  const lines = splitLines(raw);
  const target = lines[line];
  if (target === undefined) throw new Error(`No line ${line} in file`);

  const m = TASK_RE.exec(target);
  if (!m) throw new Error(`Line ${line} is not a task: ${JSON.stringify(target)}`);
  if (expectedText !== undefined && (m[6] ?? '') !== expectedText) {
    throw new Error(
      `Line ${line} changed: expected ${JSON.stringify(expectedText)}, found ${JSON.stringify(m[6] ?? '')}`,
    );
  }

  lines.splice(line, 1);
  return lines.join(eol);
}

/** Replace the text of one task, preserving its checkbox state and indentation. */
export function setTaskText(
  raw: string,
  line: number,
  text: string,
  expectedText?: string,
): string {
  const eol = detectEol(raw);
  const lines = splitLines(raw);
  const target = lines[line];
  if (target === undefined) throw new Error(`No line ${line} in file`);

  const m = TASK_RE.exec(target);
  if (!m) throw new Error(`Line ${line} is not a task: ${JSON.stringify(target)}`);
  if (expectedText !== undefined && (m[6] ?? '') !== expectedText) {
    throw new Error(
      `Line ${line} changed: expected ${JSON.stringify(expectedText)}, found ${JSON.stringify(m[6] ?? '')}`,
    );
  }

  lines[line] = `${m[1] ?? ''}${m[2] ?? '-'}${m[3] ?? ' '}[${m[4] ?? ' '}]${m[5] ?? ' '}${text}`;
  return lines.join(eol);
}
